import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { resolveSignupGate } from '@/lib/auth/signup-gate';

/**
 * Durable "an initial signup is underway" flag.
 *
 * The auth-modal state ({@link ./authModalStore}) is in-memory, so a page
 * refresh closes the signup sheet and the user looks like a plain authenticated
 * session. At the terms step that is indistinguishable from a returning user who
 * owes a re-acceptance, so the standalone `TermsGate` would wrongly pre-empt the
 * funnel. This flag survives a refresh (localStorage) and is the signal the app
 * uses to (a) reopen the signup sheet at the user's next step and (b) keep the
 * gate suppressed until signup actually completes.
 *
 * Lifecycle: set when the signup sheet opens (`start`); cleared when the funnel
 * completes — terms accepted — or the user logs out (`finish`). Returning users
 * never open the signup sheet, so they never set it and their genuine
 * re-acceptances still flow through the gate.
 */
interface SignupProgressState {
  active: boolean;
  start: () => void;
  finish: () => void;
}

const useSignupProgressStore = create<SignupProgressState>()(
  persist(
    (set) => ({
      active: false,
      start: () => set({ active: true }),
      finish: () => {
        // Every completion path (terms accepted, nothing-pending auto-complete,
        // guest done) funnels through here, so this is where an awaited signup
        // gate learns the funnel genuinely finished. The abandonment path
        // (logout → finishSignupImperative) resolves 'dismissed' first, making
        // this a no-op there.
        resolveSignupGate('completed');
        set({ active: false });
      },
    }),
    { name: 'predictions.signup-in-progress' },
  ),
);

/** True while an initial signup funnel is underway (survives refresh). */
export const useSignupInProgress = (): boolean => useSignupProgressStore((s) => s.active);

/** Mark the signup funnel as underway. Idempotent. */
export const useStartSignup = (): (() => void) => useSignupProgressStore((s) => s.start);

/** Mark the signup funnel as done (or abandoned via logout). */
export const useFinishSignup = (): (() => void) => useSignupProgressStore((s) => s.finish);

/** Imperative clear for non-React call sites (e.g. the logout handler). */
export const finishSignupImperative = (): void => {
  // Logout abandons the funnel — any awaited signup gate must not report
  // 'completed' (a replay against the dead session would just 401).
  resolveSignupGate('dismissed');
  useSignupProgressStore.getState().finish();
};
